'use client';

import { Phone, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { useFloatingVoiceDockPosition } from '@/components/voice/use-floating-voice-dock-position';
import type { VoiceAvatarLook } from '@/components/voice/voice-companion-face';
import { VoiceDockAvatar } from '@/components/voice/voice-dock-avatar';
import { getDocumentDirection } from '@/i18n/config';
import { getFloatingVoiceDockCopy } from '@/i18n/floating-voice-dock-copy';
import { getIdleVoiceDockCopy } from '@/i18n/idle-voice-dock-copy';
import { cn } from '@/lib/utils';

export type IdleVoiceDockProps = {
  locale: string;
  assistantName: string;
  look: VoiceAvatarLook;
  isStarting?: boolean;
  isCallBlocked?: boolean;
  callBlockedMessage?: string;
  positionKey?: string;
  onStart: () => void;
  onDismiss: () => void;
};

export function IdleVoiceDock({
  locale,
  assistantName,
  look,
  isStarting = false,
  isCallBlocked = false,
  callBlockedMessage,
  positionKey,
  onStart,
  onDismiss,
}: IdleVoiceDockProps) {
  const dockPosition = useFloatingVoiceDockPosition(false, {
    positionKey,
    avoidMobileComposer: true,
  });
  const {
    boundaryRef,
    panelRef,
    position,
    canAnimatePosition,
    dragging,
    ready,
    point,
  } = dockPosition;
  const copy = getIdleVoiceDockCopy(locale, assistantName);
  const voiceCopy = getFloatingVoiceDockCopy(locale);
  const startLabel = isStarting ? voiceCopy.connecting : copy.callYourImai;
  return (
    <div
      ref={boundaryRef}
      data-slot="floating-voice-boundary"
      data-idle-voice-boundary
      className="sidebar-offset-transition pointer-events-none fixed inset-y-0 right-0 left-0 z-40 lg:left-[var(--sidebar-width,0px)]"
    >
      <section
        ref={panelRef}
        data-slot="idle-voice-dock"
        data-dock-corner={position}
        aria-busy={isStarting}
        dir={getDocumentDirection(locale)}
        className={cn(
          'pointer-events-none absolute top-0 left-0 flex w-36 max-w-[calc(100vw-1.5rem)] flex-col items-center pt-4 pb-2',
          canAnimatePosition && !dragging
            ? 'transition-transform duration-200 ease-out motion-reduce:transition-none'
            : 'transition-none',
          ready ? 'visible' : 'invisible'
        )}
        style={{
          transform: `translate3d(${point.x}px, ${point.y}px, 0)`,
        }}
      >
        <div className="pointer-events-auto relative mb-1 flex size-16 items-center justify-center [filter:drop-shadow(0_0_4px_var(--background))_drop-shadow(0_0_8px_var(--background))]">
          <VoiceDockAvatar
            status="idle"
            inputMode="vad"
            micMuted={false}
            pttActive={false}
            look={look}
            disabled={isStarting}
            moveHint={voiceCopy.moveHint}
            position={dockPosition}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            data-slot="idle-voice-dismiss"
            aria-label={copy.hideAvatar}
            title={copy.hideAvatar}
            disabled={isStarting}
            onClick={onDismiss}
            className="border-border bg-card hover:bg-accent absolute -top-1 -left-2 size-5 rounded-full border shadow-sm after:absolute after:-inset-0.5 after:rounded-full after:content-['']"
          >
            <X className="size-2.5" aria-hidden />
          </Button>
        </div>
        <Button
          type="button"
          variant="outline"
          data-slot="idle-voice-start"
          aria-label={startLabel}
          title={isCallBlocked ? callBlockedMessage : startLabel}
          disabled={isStarting}
          onClick={onStart}
          className="bg-card/95 pointer-events-auto h-8 max-w-full gap-1.5 rounded-full px-2.5 shadow-lg backdrop-blur-md"
        >
          {isStarting ? (
            <Loader
              className="size-3.5 shrink-0"
              aria-label={voiceCopy.connecting}
            />
          ) : (
            <Phone className="size-3.5 shrink-0" aria-hidden />
          )}
          <span className="truncate text-xs">{startLabel}</span>
        </Button>
      </section>
    </div>
  );
}
